import type { AttemptResult, Difficulty } from "@/domain";

const DIFFICULTIES = [1, 2, 3, 4, 5, 6] as const;

export const DEFAULT_TARGET_DURATION_MS = 120_000;

export const ATTEMPT_SCORE_WEIGHTS = {
  accuracy: 70,
  independence: 20,
  pace: 10
} as const;

export const SUPPORT_PENALTIES = {
  hint: 4,
  retry: 6
} as const;

export type AttemptMetrics = Pick<
  AttemptResult,
  "correctRate" | "hintCount" | "retryCount" | "durationMs"
>;

export interface AttemptScoringOptions {
  targetDurationMs?: number;
}

export function clampScore(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }

  return Math.min(100, Math.max(0, value));
}

export function clampDifficulty(value: number): Difficulty {
  if (Number.isNaN(value) || value === Number.NEGATIVE_INFINITY) {
    return DIFFICULTIES[0];
  }

  if (value === Number.POSITIVE_INFINITY) {
    return DIFFICULTIES[5];
  }

  const index = Math.min(
    DIFFICULTIES.length - 1,
    Math.max(0, Math.round(value) - 1)
  );

  return DIFFICULTIES[index] ?? DIFFICULTIES[0];
}

function clampUnit(value: number): number {
  return clampScore(value * 100) / 100;
}

function normalizeCount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

function resolveTargetDuration(targetDurationMs: number | undefined): number {
  return targetDurationMs !== undefined &&
    Number.isFinite(targetDurationMs) &&
    targetDurationMs > 0
    ? targetDurationMs
    : DEFAULT_TARGET_DURATION_MS;
}

export function calculateAttemptScore(
  attempt: AttemptMetrics,
  options: AttemptScoringOptions = {}
): number {
  const targetDurationMs = resolveTargetDuration(options.targetDurationMs);
  const durationMs = Number.isFinite(attempt.durationMs)
    ? Math.max(0, attempt.durationMs)
    : targetDurationMs * 2;

  const accuracyScore =
    clampUnit(attempt.correctRate) * ATTEMPT_SCORE_WEIGHTS.accuracy;
  const independenceScore = Math.max(
    0,
    ATTEMPT_SCORE_WEIGHTS.independence -
      normalizeCount(attempt.hintCount) * SUPPORT_PENALTIES.hint -
      normalizeCount(attempt.retryCount) * SUPPORT_PENALTIES.retry
  );
  const paceScore =
    clampUnit(2 - durationMs / targetDurationMs) * ATTEMPT_SCORE_WEIGHTS.pace;

  return Math.round(
    clampScore(accuracyScore + independenceScore + paceScore)
  );
}
